import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Button, Modal, SpaceBetween, Header, Tabs, Spinner, StatusIndicator } from '@amzn/awsui-components-react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { renderStatus, getStatus, getOneWBStatusBasedOnCustomAttributes } from './Utility';
import { getFriscoTransaction } from '../Templates/Utils/AppActivityUtils';
import { getFlexicoTransaction } from 'src/utilities/FlexicoAPIs';
import { getDocumentFromVault } from 'src/Ateam-BI/Vault/VaultAPIs';
import { VAULT_SERVICES } from 'src/Ateam-BI/Vault/constants';
import { cleanS3PathForDownload } from 'src/utilities/S3Utils';
import EmbedSingleFile from 'src/generic-components/EmbedResults/EmbedSingleFile';

/**
 * ViewSupportModal - Shows deliverables for selected sections via ag-grid.
 * Checkbox selection on rows; checked items become tabs below.
 *
 * Props:
 *   visible (bool)
 *   onDismiss (func)
 *   selectedSections (array) - raw grid rows with service_mappings
 *   deliverableOptions (array) - [{label, value, description, filteringTags, ...}]
 *   tracksData (array) - track records for status
 *   wbType (string) - "Pre-WB" or "Post-WB"
 *   loading (bool) - whether deliverables are still loading
 */
const ViewSupportModal = ({ visible, onDismiss, selectedSections = [], deliverableOptions = [], tracksData = [], wbType = '', loading = false }) => {
    const gridRef = useRef();
    const [selectedItems, setSelectedItems] = useState([]);
    const [activeTabId, setActiveTabId] = useState(null);

    // Compute enriched deliverables from props reactively
    const deliverables = React.useMemo(() => {
        if (!selectedSections.length || !deliverableOptions.length) return [];
        const rows = [];
        selectedSections.forEach(row => {
            const mappings = row.service_mappings || [];
            const sectionDeliverables = deliverableOptions.filter(o =>
                mappings.some(sm => String(sm.association_id) === o.value && sm.association_type === o.deliverableType)
            );
            sectionDeliverables.forEach(d => {
                rows.push({ ...d, sectionTitle: row.title, sectionPage: row.page });
            });
        });

        const wbTypeKey = wbType === 'Pre-WB' ? 'PreWB' : 'PostWB';
        return rows.map(d => {
            const mappingId = d.value;
            const track = tracksData.find(t =>
                (t.mapping_id && String(t.mapping_id) === mappingId)
            );
            let status = null;
            if (track) {
                status = getStatus(track, getOneWBStatusBasedOnCustomAttributes(track, wbTypeKey));
            }
            return { ...d, statusData: status };
        });
    }, [selectedSections, deliverableOptions, tracksData, wbType]);

    const downloadAllFiles = async (data) => {
        const fileId = data?.statusData?.file_id;
        if (!fileId) return;
        const transaction = transactionCache[fileId];
        if (!transaction) return;

        const isFrisco = data.deliverableType === 'FRISCO_MAPPING_ID';
        const service = isFrisco ? VAULT_SERVICES.FRISCO : VAULT_SERVICES.FLEXICO;

        const filesToDownload = [];
        if (transaction.filePath) filesToDownload.push(transaction.filePath);
        if (transaction.rawFilePath) filesToDownload.push(transaction.rawFilePath);
        if (transaction.supportingDocuments && Array.isArray(transaction.supportingDocuments)) {
            transaction.supportingDocuments.forEach(doc => filesToDownload.push(doc));
        }

        for (const filePath of filesToDownload) {
            try {
                const fn = cleanS3PathForDownload(filePath);
                const arr = fn.split('.');
                const contentType = arr[arr.length - 1];
                const serviceParams = { file_path: `${decodeURIComponent(fn).replaceAll('ABCPLUSTEMP', '+')}` };
                // service_id is the process/template identifier: processId for FRISCO, flexId for FLEXICO
                const serviceIdentifier = isFrisco ? transaction.processId : transaction.flexId;
                if (serviceIdentifier) serviceParams.service_id = `${serviceIdentifier}`;
                if (transaction.appId) serviceParams.app_id = `${transaction.appId}`;
                const document = await getDocumentFromVault(contentType, service, serviceParams);
                if (document?.fileUrl) window.open(document.fileUrl);
            } catch (e) {
                console.error('Download failed for', filePath, e);
            }
        }
    };

    const columnDefs = [
        { headerName: '', checkboxSelection: true, headerCheckboxSelection: true, width: 50, sortable: false, filter: false, resizable: false },
        { field: 'sectionTitle', headerName: 'Section', width: 350 },
        { field: 'label', headerName: 'Support Name', width: 250 },
        { field: 'reference', headerName: 'Reference', width: 200, valueGetter: (params) => (params.data?.filteringTags || []).join(', ') || params.data?.description || '-' },
        { field: 'status', headerName: 'Status', width: 250, cellRenderer: (params) => {
            const serviceType = params.data?.deliverableType === 'FLEXICO_MAPPING_ID' ? 'flexico' : 'frisco';
            return renderStatus(params.data?.statusData, serviceType, true);
        }},
        { headerName: '', width: 80, sortable: false, filter: false, resizable: false, cellRenderer: (params) => {
            const fileId = params.data?.statusData?.file_id;
            if (!fileId || !transactionCache[fileId]) return null;
            return <Button iconName="download" variant="inline-icon" onClick={() => downloadAllFiles(params.data)} />;
        }},
    ];

    const defaultColDef = { sortable: true, filter: true, resizable: true };

    const onSelectionChanged = useCallback(() => {
        const selected = gridRef.current?.api?.getSelectedRows() || [];
        setSelectedItems(selected);
        if (selected.length > 0 && !selected.find(s => s.value === activeTabId)) {
            setActiveTabId(selected[0].value);
        }
        if (selected.length === 0) {
            setActiveTabId(null);
        }
    }, [activeTabId]);

    // Auto-select first row when deliverables load
    useEffect(() => {
        if (!deliverables.length || !gridRef.current?.api) return;
        setTimeout(() => {
            const firstNode = gridRef.current?.api?.getDisplayedRowAtIndex(0);
            if (firstNode) {
                firstNode.setSelected(true);
            }
        }, 100);
    }, [deliverables]);

    const [transactionCache, setTransactionCache] = useState({});
    const [loadingFileId, setLoadingFileId] = useState(null);

    // Single reusable fetch function
    const fetchTransaction = (fileId, isFlexico) => {
        if (!fileId || transactionCache[fileId]) return;
        if (isFlexico) {
            getFlexicoTransaction(fileId, (result) => {
                setTransactionCache(prev => ({ ...prev, [fileId]: result?.data?.getFlexicoTransaction }));
                setLoadingFileId(prev => prev === fileId ? null : prev);
            }, () => { setLoadingFileId(prev => prev === fileId ? null : prev); });
        } else {
            getFriscoTransaction(fileId, (result) => {
                setTransactionCache(prev => ({ ...prev, [fileId]: result?.data?.getFRISCOTransactions }));
                setLoadingFileId(prev => prev === fileId ? null : prev);
            }, () => { setLoadingFileId(prev => prev === fileId ? null : prev); });
        }
    };

    // Set loading indicator when tab switches to an uncached transaction
    useEffect(() => {
        if (!activeTabId) return;
        const item = selectedItems.find(d => d.value === activeTabId);
        const fileId = item?.statusData?.file_id;
        if (!fileId || transactionCache[fileId]) return;
        setLoadingFileId(fileId);
        fetchTransaction(fileId, item.deliverableType === 'FLEXICO_MAPPING_ID');
    }, [activeTabId, selectedItems]);

    // Pre-fetch all transactions so download buttons appear immediately
    useEffect(() => {
        if (!deliverables.length) return;
        deliverables.forEach(d => {
            const fileId = d?.statusData?.file_id;
            fetchTransaction(fileId, d.deliverableType === 'FLEXICO_MAPPING_ID');
        });
    }, [deliverables]);

    const renderTabContent = (d) => {
        const fileId = d.statusData?.file_id;
        if (!fileId) return <Box padding="l">No file submitted yet for <strong>{d.label}</strong>.</Box>;

        if (loadingFileId === fileId) return <Box padding="l"><Spinner /> Loading file details...</Box>;

        const transaction = transactionCache[fileId];
        if (!transaction) return <Box padding="l"><Spinner /> Loading file details...</Box>;

        const isFlexico = d.deliverableType === 'FLEXICO_MAPPING_ID';
        const bucketName = isFlexico ? transaction.bucketName : transaction.bucket_name;
        const serviceId = isFlexico ? transaction.flexId : transaction.processId;
        const appId = transaction.appId;
        const service = isFlexico ? VAULT_SERVICES.FLEXICO : VAULT_SERVICES.FRISCO;

        // Collect all file paths
        const allFiles = [];
        if (transaction.filePath) {
            transaction.filePath.split(',').forEach(fp => allFiles.push(fp.trim()));
        }
        if (transaction.rawFilePath) {
            allFiles.push(transaction.rawFilePath);
        }
        if (transaction.supportingDocuments && Array.isArray(transaction.supportingDocuments)) {
            transaction.supportingDocuments.forEach(doc => { if (doc) allFiles.push(doc); });
        }

        if (allFiles.length === 0) {
            return <Box padding="l">No files available for this deliverable.</Box>;
        }

        // Build unified file tabs - each file rendered directly via EmbedSingleFile (no nested tabs)
        const fileTabs = allFiles.map((fp, idx) => {
            const fileName = fp.split('/').pop() || 'File';
            return {
                id: `file-${idx}`,
                label: fileName,
                content: <EmbedSingleFile filePath={fp} bucket={bucketName} service={service} serviceId={serviceId} appId={appId} />,
            };
        });

        // If there's only one file, render it directly without sub-tabs
        if (fileTabs.length === 1) {
            return fileTabs[0].content;
        }

        return <Tabs tabs={fileTabs} />;
    };

    const tabs = selectedItems.map(d => ({
        id: d.value,
        label: d.label,
        content: renderTabContent(d),
    }));

    const handleDismiss = () => {
        setSelectedItems([]);
        setActiveTabId(null);
        onDismiss();
    };

    return (
        <Modal
            visible={visible}
            onDismiss={handleDismiss}
            size="max"
            header={<Header variant="h2">Submitted support — {wbType}</Header>}
        >
            <SpaceBetween direction="vertical" size="m">
                <Box variant="p" color="text-body-secondary">Prepared and submitted by accounting teams</Box>
                {loading ? (
                    <Box padding="l"><Spinner /> Loading deliverables...</Box>
                ) : deliverables.length === 0 ? (
                    <Box padding="l">No deliverables mapped to the selected sections.</Box>
                ) : (
                    <>
                        <div className="ag-theme-alpine" style={{ width: '100%', height: '300px' }}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={deliverables}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                rowSelection="multiple"
                                suppressRowClickSelection={true}
                                onSelectionChanged={onSelectionChanged}
                                animateRows={true}
                                suppressCellFocus={true}
                            />
                        </div>
                        {tabs.length > 0 && (
                            <Tabs
                                activeTabId={activeTabId}
                                onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
                                tabs={tabs}
                            />
                        )}
                    </>
                )}
            </SpaceBetween>
        </Modal>
    );
};

export default ViewSupportModal;
